/**
 * Push notifications (feature #8) — multi-device FCM registration + handling.
 *
 * Web:    firebase/messaging getToken(vapidKey) + onMessage (foreground).
 * Native: @capacitor/push-notifications register() → 'registration' event
 *         gives the FCM token; 'pushNotificationReceived' (foreground) and
 *         'pushNotificationActionPerformed' (tap → deep link).
 *
 * FCM TOKEN PERSISTENCE (FREE-TIER MODEL):
 *   There are no Cloud Functions on the Spark plan, so tokens are persisted with
 *   a DIRECT Firestore write to the caller's OWN `users/{uid}` doc — security
 *   rules allow a user to self-update their doc as long as `status`/`id` stay
 *   unchanged. See `addOwnFcmToken` / `removeOwnFcmToken` in `lib/repo.ts`
 *   (FCM_TOKEN_LIMIT enforced client-side). This module's job is just to obtain
 *   the token, cache it locally (Capacitor Preferences), and hand it to
 *   `onToken`; the caller (AppLifecycle) does the Firestore write once the user
 *   is signed in and approved. On logout AuthContext removes the cached token.
 */
import { Preferences } from '@capacitor/preferences';
import { getToken, onMessage } from 'firebase/messaging';
import { getWebMessaging } from './firebase';
import { vapidKey } from './env';
import { isNative } from './platform';

const TOKEN_CACHE_KEY = 'mitang.fcmToken';

export interface PushDeepLink {
  /** e.g. "product/abc" or "category/LAPTOPS" — matched by the router. */
  path: string;
}

export interface PushHandlers {
  /** Called with a freshly obtained FCM token (may be called more than once). */
  onToken?: (token: string) => void | Promise<void>;
  /** Foreground message → show an in-app banner. */
  onForeground?: (payload: { title?: string; body?: string; deepLink?: string }) => void;
  /** User tapped a notification → navigate to the deep link. */
  onOpen?: (link: PushDeepLink) => void;
}

async function cacheToken(token: string): Promise<void> {
  await Preferences.set({ key: TOKEN_CACHE_KEY, value: token });
}

export async function getCachedToken(): Promise<string | null> {
  const { value } = await Preferences.get({ key: TOKEN_CACHE_KEY });
  return value ?? null;
}

/** Normalize a deepLink string into a router-usable path. */
export function normalizeDeepLink(raw: string | undefined): PushDeepLink | null {
  if (!raw) return null;
  const path = raw.replace(/^\/+/, '').trim();
  if (!path) return null;
  return { path };
}

/* -------------------------------------------------------------------------- */
/*  Native (Capacitor Android)                                                */
/* -------------------------------------------------------------------------- */

async function initNativePush(handlers: PushHandlers): Promise<() => void> {
  // Dynamically import so web bundles don't pull the native plugin surface.
  const { PushNotifications } = await import('@capacitor/push-notifications');

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') {
    return () => undefined;
  }
  await PushNotifications.register();

  const regHandle = await PushNotifications.addListener('registration', (token) => {
    void cacheToken(token.value);
    void handlers.onToken?.(token.value);
  });

  const errHandle = await PushNotifications.addListener('registrationError', () => {
    // swallow; token simply won't be available
  });

  const recvHandle = await PushNotifications.addListener(
    'pushNotificationReceived',
    (notification) => {
      handlers.onForeground?.({
        title: notification.title ?? undefined,
        body: notification.body ?? undefined,
        deepLink:
          (notification.data?.deepLink as string | undefined) ??
          (notification.data?.deep_link as string | undefined),
      });
    },
  );

  const actionHandle = await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action) => {
      const raw =
        (action.notification.data?.deepLink as string | undefined) ??
        (action.notification.data?.deep_link as string | undefined);
      const link = normalizeDeepLink(raw);
      if (link) handlers.onOpen?.(link);
    },
  );

  return () => {
    void regHandle.remove();
    void errHandle.remove();
    void recvHandle.remove();
    void actionHandle.remove();
  };
}

/* -------------------------------------------------------------------------- */
/*  Web (firebase/messaging)                                                  */
/* -------------------------------------------------------------------------- */

async function initWebPush(handlers: PushHandlers): Promise<() => void> {
  const messaging = await getWebMessaging();
  if (!messaging) return () => undefined;

  // Requires Notification permission + a registered service worker
  // (public/firebase-messaging-sw.js) for background messages.
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return () => undefined;
  }

  let swReg: ServiceWorkerRegistration | undefined;
  if ('serviceWorker' in navigator) {
    try {
      swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch {
      swReg = undefined;
    }
  }

  try {
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    });
    if (token) {
      await cacheToken(token);
      await handlers.onToken?.(token);
    }
  } catch {
    // getToken can fail if permission/SW/vapid are missing — non-fatal.
  }

  const unsub = onMessage(messaging, (payload) => {
    handlers.onForeground?.({
      title: payload.notification?.title,
      body: payload.notification?.body,
      deepLink: payload.data?.deepLink ?? payload.data?.deep_link,
    });
  });

  return () => unsub();
}

/**
 * Initialize push for the current platform. Returns a teardown function.
 * Call after login (feature #8).
 */
export async function initPush(handlers: PushHandlers): Promise<() => void> {
  try {
    return isNative ? await initNativePush(handlers) : await initWebPush(handlers);
  } catch {
    return () => undefined;
  }
}
